/**
 * The notes feed — one component behind every notes surface.
 *
 * Modelled on Slack, because the shape of the problem is the same: a channel of
 * short messages that occasionally turn into conversations.
 *   - replies live in a THREAD, folded behind a "3 replies" summary, so the feed
 *     lists notes rather than arguments
 *   - each message carries a hover toolbar (reply · resolve · save · ⋮)
 *   - Edit and Delete sit under the ⋮, and deleting asks what for
 *
 * Ours on top of that: a thread can be RESOLVED, which is what turns a feed of
 * remarks into a to-do list an operator can clear.
 *
 * Any admin may write, reply, resolve and save; only the author (or a super
 * admin) may edit or delete. The database enforces that, and this component
 * mirrors it by not offering the buttons — a rule the UI ignores is a rule the
 * user meets as an error toast.
 *
 * Scope in, feed out — the caller decides whether it renders inside a tab
 * (NotesPanel) or a drawer, and never has to know how notes are stored.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Bookmark,
  BookmarkCheck,
  Check,
  CheckCircle2,
  Loader2,
  MessageSquare,
  MessageSquarePlus,
  MoreVertical,
  Paperclip,
  Pencil,
  RotateCcw,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  NOTE_FILE_MAX_BYTES,
  formatBytes,
  uploadNoteAttachment,
  useAddNote,
  useDeleteNote,
  useEditNote,
  useEntityNotes,
  useResolveNote,
  useToggleNoteSave,
  type NoteFile,
  type NoteRow,
  type NoteScope,
  type NoteThread as NoteThreadModel,
} from "@/hooks/v2/useEntityNotes";
import {
  NoteAttachment,
  NoteAvatar,
  absoluteTime,
  personLabel,
  relativeTime,
} from "./note-ui";

type Filter = "open" | "resolved" | "saved" | "all";

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

// ─── Auto-growing textarea ──────────────────────────────────────────────────
// A note is usually one line and occasionally twelve. A fixed box is wrong for
// both; growing to fit keeps the composer out of the way until it is needed.

function AutoTextarea({
  value,
  onChange,
  onSubmit,
  placeholder,
  autoFocus,
  disabled,
  minRows = 1,
  className,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  minRows?: number;
  className?: string;
  ariaLabel: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (onSubmit && (e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "w-full resize-none bg-transparent text-[14px] leading-relaxed text-body outline-none",
        "placeholder:text-muted-foreground disabled:opacity-60",
        className
      )}
    />
  );
}

// ─── Composer ───────────────────────────────────────────────────────────────

function Composer({
  scope,
  parentId = null,
  placeholder,
  autoFocus,
  onDone,
  onCancel,
  compact,
}: {
  scope: NoteScope;
  parentId?: string | null;
  placeholder: string;
  autoFocus?: boolean;
  onDone?: () => void;
  onCancel?: () => void;
  compact?: boolean;
}) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const add = useAddNote(scope);
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = uploading || add.isPending;
  const canSubmit = (body.trim().length > 0 || !!file) && !busy && !!user?.id;

  const submit = useCallback(async () => {
    if (!canSubmit || !user?.id) return;
    setUploading(true);
    try {
      let uploaded: NoteFile | null = null;
      if (file) uploaded = await uploadNoteAttachment(scope, file);
      await add.mutateAsync({
        body: body.trim() || null,
        createdBy: user.id,
        parentId,
        file: uploaded,
      });
      setBody("");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onDone?.();
    } catch (e) {
      toast.error(
        errorText(
          e,
          t("admin.notes.error.saveFailed", "Could not save the note.")
        )
      );
    } finally {
      setUploading(false);
    }
  }, [add, body, canSubmit, file, onDone, parentId, scope, user?.id, t]);

  function pick(next: File | null) {
    if (next && next.size > NOTE_FILE_MAX_BYTES) {
      toast.error(
        `“${next.name}” ${t("admin.notes.error.attachmentTooLargePrefix", "is")} ${formatBytes(next.size)} — ${t("admin.notes.error.attachmentTooLargeSuffix", "attachments are limited to")} ${formatBytes(NOTE_FILE_MAX_BYTES)}.`
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFile(next);
  }

  return (
    <div
      className={cn(
        "flex gap-3 rounded-2xl border border-border-subtle bg-white p-3 transition-shadow focus-within:border-turkis-strong focus-within:shadow-[0_0_0_3px_hsl(var(--turkis-fill))]",
        compact && "rounded-xl p-2.5"
      )}
    >
      <NoteAvatar
        person={
          user
            ? {
                id: user.id,
                name: (user.user_metadata?.name as string | undefined) ?? null,
                email: user.email ?? null,
              }
            : null
        }
        size={compact ? "sm" : "md"}
      />
      <div className="min-w-0 flex-1">
        <AutoTextarea
          value={body}
          onChange={setBody}
          onSubmit={submit}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={busy}
          ariaLabel={
            parentId
              ? t("admin.notes.ariaWriteReply", "Write a reply")
              : t("admin.notes.ariaWriteNote", "Write a note")
          }
          className={compact ? "text-[13.5px]" : undefined}
        />

        {file && (
          <div className="mt-2 flex items-center gap-2 rounded-lg bg-sand px-2.5 py-1.5">
            <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-[12.5px] font-medium text-body">
              {file.name}
            </span>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {formatBytes(file.size)}
            </span>
            <button
              type="button"
              onClick={() => pick(null)}
              aria-label={t(
                "admin.notes.removeAttachment",
                "Remove attachment"
              )}
              className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle px-3 py-1.5 text-[12.5px] font-semibold text-body-soft transition-colors hover:border-turkis-strong hover:text-petrol disabled:opacity-50"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {t("admin.notes.attach", "Attach")}
            </button>
            {onCancel && (
              <button
                type="button"
                disabled={busy}
                onClick={onCancel}
                className="rounded-full px-3 py-1.5 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:text-body disabled:opacity-50"
              >
                {t("admin.notes.cancel", "Cancel")}
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => pick(e.currentTarget.files?.[0] ?? null)}
            />
          </div>

          <button
            type="button"
            disabled={!canSubmit}
            onClick={submit}
            title="⌘/Ctrl + Enter"
            className="inline-flex items-center gap-1.5 rounded-full bg-petrol px-4 py-1.5 text-[12.5px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {parentId
              ? t("admin.notes.reply", "Reply")
              : t("admin.notes.add", "Add")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── One note (root body or reply) ──────────────────────────────────────────

function NoteEntry({
  note,
  scope,
  size = "md",
  canModify,
  canWrite,
  onReply,
  resolved,
  onToggleResolve,
  resolveBusy,
}: {
  note: NoteRow;
  scope: NoteScope;
  size?: "sm" | "md";
  canModify: boolean;
  canWrite: boolean;
  /** Roots only — a reply cannot start a thread of its own. */
  onReply?: () => void;
  resolved?: boolean;
  onToggleResolve?: () => void;
  resolveBusy?: boolean;
}) {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const edit = useEditNote(scope);
  const del = useDeleteNote(scope);
  const save = useToggleNoteSave(scope);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body ?? "");
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function saveEdit() {
    const next = draft.trim();
    if (!next || next === (note.body ?? "")) {
      setEditing(false);
      setDraft(note.body ?? "");
      return;
    }
    try {
      await edit.mutateAsync({ id: note.id, body: next });
      setEditing(false);
    } catch (e) {
      toast.error(
        errorText(
          e,
          t("admin.notes.error.saveFailed", "Could not save the note.")
        )
      );
    }
  }

  async function toggleSave() {
    if (!user?.id) return;
    try {
      await save.mutateAsync({
        id: note.id,
        saved: !note.saved,
        userId: user.id,
      });
    } catch (e) {
      toast.error(
        errorText(
          e,
          t(
            "admin.notes.error.saveToggleFailed",
            "Could not update your saved notes."
          )
        )
      );
    }
  }

  return (
    <div className="group/note relative flex gap-3">
      {!editing && (
        <NoteToolbar
          canWrite={canWrite}
          canModify={canModify}
          saved={note.saved}
          savePending={save.isPending}
          onToggleSave={toggleSave}
          onReply={onReply}
          resolved={resolved}
          onToggleResolve={onToggleResolve}
          resolveBusy={resolveBusy}
          onEdit={
            note.body
              ? () => {
                  setDraft(note.body ?? "");
                  setEditing(true);
                }
              : undefined
          }
          onDelete={() => setDeleteOpen(true)}
        />
      )}

      <DeleteNoteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        note={note}
        pending={del.isPending}
        onConfirm={async (reason) => {
          try {
            await del.mutateAsync({ id: note.id, reason });
            setDeleteOpen(false);
            toast.success(t("admin.notes.deleted", "Note deleted."));
          } catch (e) {
            toast.error(
              errorText(
                e,
                t(
                  "admin.notes.error.deleteFailed",
                  "Could not delete the note."
                )
              )
            );
          }
        }}
      />

      <NoteAvatar person={note.author} size={size} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span
            className={cn(
              "font-bold text-heading",
              size === "sm" ? "text-[13px]" : "text-[14px]"
            )}
          >
            {personLabel(note.author)}
          </span>
          <span
            className="text-[12px] text-muted-foreground"
            title={absoluteTime(note.created_at)}
          >
            {relativeTime(note.created_at)}
          </span>
          {note.edited_at && (
            <span
              className="text-[11.5px] italic text-muted-foreground"
              title={`${t("admin.notes.editedAt", "Edited")} ${absoluteTime(note.edited_at)}`}
            >
              {t("admin.notes.editedLabel", "edited")}
            </span>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5 rounded-xl border border-turkis-strong bg-white p-2.5">
            <AutoTextarea
              value={draft}
              onChange={setDraft}
              onSubmit={saveEdit}
              autoFocus
              disabled={edit.isPending}
              ariaLabel={t("admin.notes.ariaEditNote", "Edit note")}
            />
            <div className="mt-2 flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraft(note.body ?? "");
                }}
                className="rounded-full px-3 py-1 text-[12.5px] font-semibold text-muted-foreground hover:text-body"
              >
                {t("admin.notes.cancel", "Cancel")}
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={edit.isPending}
                className="inline-flex items-center gap-1.5 rounded-full bg-petrol px-3.5 py-1 text-[12.5px] font-bold text-white hover:opacity-90 disabled:opacity-40"
              >
                {edit.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                {t("admin.notes.save", "Save")}
              </button>
            </div>
          </div>
        ) : (
          note.body && (
            <p
              className={cn(
                "mt-0.5 whitespace-pre-wrap break-words text-body",
                size === "sm"
                  ? "text-[13.5px] leading-relaxed"
                  : "text-[14px] leading-relaxed"
              )}
            >
              {note.body}
            </p>
          )
        )}

        <NoteAttachment note={note} />
      </div>
    </div>
  );
}

// ─── Hover toolbar ──────────────────────────────────────────────────────────

/**
 * Slack's message toolbar: it floats over the top-right corner of the note and
 * only appears on hover, so a feed reads as writing rather than as a grid of
 * buttons. Everything in it stays reachable by keyboard — `focus-within` keeps
 * it visible while tabbing, which a pure `:hover` reveal would not.
 */
function NoteToolbar({
  canWrite,
  canModify,
  saved,
  savePending,
  onToggleSave,
  onReply,
  resolved,
  onToggleResolve,
  resolveBusy,
  onEdit,
  onDelete,
}: {
  canWrite: boolean;
  canModify: boolean;
  saved: boolean;
  savePending: boolean;
  onToggleSave: () => void;
  onReply?: () => void;
  resolved?: boolean;
  onToggleResolve?: () => void;
  resolveBusy?: boolean;
  onEdit?: () => void;
  onDelete: () => void;
}) {
  const { t } = useLanguage();
  const showOverflow = canModify && (onEdit || onDelete);
  if (!canWrite && !showOverflow) return null;

  return (
    <div
      className={cn(
        "absolute -top-3.5 right-0 z-10 flex items-center gap-0.5 rounded-lg border border-border-subtle bg-white p-0.5 shadow-md",
        "opacity-0 transition-opacity focus-within:opacity-100 group-hover/note:opacity-100",
        // Touch has no hover: on coarse pointers the toolbar is simply always
        // there, rather than unreachable.
        "[@media(hover:none)]:opacity-100"
      )}
    >
      {canWrite && onReply && (
        <ToolbarButton
          label={t("admin.notes.replyInThread", "Reply in thread")}
          icon={MessageSquare}
          onClick={onReply}
        />
      )}
      {canWrite && onToggleResolve && (
        <ToolbarButton
          label={
            resolved
              ? t("admin.notes.reopen", "Reopen")
              : t("admin.notes.markResolved", "Mark resolved")
          }
          icon={resolved ? RotateCcw : CheckCircle2}
          onClick={onToggleResolve}
          busy={resolveBusy}
          active={resolved}
        />
      )}
      {canWrite && (
        <ToolbarButton
          label={
            saved
              ? t("admin.notes.removeFromSaved", "Remove from saved")
              : t("admin.notes.saveForLater", "Save for later")
          }
          icon={saved ? BookmarkCheck : Bookmark}
          onClick={onToggleSave}
          busy={savePending}
          active={saved}
        />
      )}
      {showOverflow && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("admin.notes.moreActions", "More actions")}
              className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sand hover:text-body data-[state=open]:bg-sand data-[state=open]:text-body"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {onEdit && (
              <DropdownMenuItem onSelect={onEdit} className="gap-2">
                <Pencil className="h-4 w-4" />
                {t("admin.notes.editMessage", "Edit message")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={onDelete}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              {t("admin.notes.deleteMessage", "Delete message")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  busy,
  active,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  busy?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "grid h-7 w-7 place-items-center rounded-md transition-colors disabled:opacity-50",
        active
          ? "text-turkis-strong hover:bg-turkis-fill-soft"
          : "text-muted-foreground hover:bg-sand hover:text-body"
      )}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Icon className="h-4 w-4" />
      )}
    </button>
  );
}

// ─── Delete, with a reason ──────────────────────────────────────────────────

/**
 * Deleting asks why, and the answer is kept on the row (a soft delete).
 * Notes are where operators record verbal agreements and disputes; "it used to
 * say something and now it does not" is exactly the question someone asks three
 * months later, and an unexplained disappearance cannot answer it.
 */
function DeleteNoteDialog({
  open,
  onOpenChange,
  note,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  note: NoteRow;
  pending: boolean;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useLanguage();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const canConfirm = reason.trim().length > 0 && !pending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("admin.notes.deleteDialog.title", "Delete this note?")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "admin.notes.deleteDialog.description",
              "It disappears from the feed. The text and your reason stay on the record, so the deletion can be accounted for later."
            )}
          </DialogDescription>
        </DialogHeader>

        {note.body && (
          <blockquote className="max-h-28 overflow-y-auto rounded-lg border-l-2 border-border-subtle bg-sand/60 px-3 py-2 text-[13px] italic leading-relaxed text-body-soft">
            {note.body}
          </blockquote>
        )}

        <div className="space-y-1.5">
          <label
            htmlFor="delete-note-reason"
            className="text-[12.5px] font-semibold text-body"
          >
            {t("admin.notes.deleteDialog.reasonLabel", "Reason for deleting")}
          </label>
          <Textarea
            id="delete-note-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t(
              "admin.notes.deleteDialog.reasonPlaceholder",
              "e.g. wrong record, superseded by the note below, contained a typo…"
            )}
            className="min-h-[76px] resize-y text-[13.5px]"
            autoFocus
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t("admin.notes.cancel", "Cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={!canConfirm}
            onClick={() => onConfirm(reason)}
            className="gap-2"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("admin.notes.deleteDialog.confirm", "Delete note")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── One thread ─────────────────────────────────────────────────────────────

function ThreadCard({
  thread,
  scope,
  canWrite,
  canModify,
}: {
  thread: NoteThreadModel;
  scope: NoteScope;
  canWrite: boolean;
  canModify: (note: NoteRow) => boolean;
}) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const resolve = useResolveNote(scope);
  // Slack's rule: the feed lists notes, not conversations. Replies stay folded
  // behind a summary so ten notes read as ten lines instead of one scroll of
  // back-and-forth — you open the thread you care about.
  const [open, setOpen] = useState(false);
  const [replying, setReplying] = useState(false);
  const resolved = !!thread.resolved_at;
  const replyCount = thread.replies.length;

  function openThread(withComposer: boolean) {
    setOpen(true);
    if (withComposer) setReplying(true);
  }

  async function toggleResolved() {
    try {
      await resolve.mutateAsync({
        id: thread.id,
        resolved: !resolved,
        actorId: user?.id ?? null,
      });
    } catch (e) {
      toast.error(
        errorText(
          e,
          t("admin.notes.error.updateFailed", "Could not update the note.")
        )
      );
    }
  }

  return (
    <div
      className={cn(
        "rounded-2xl border p-3.5 transition-colors",
        resolved
          ? "border-border-subtle/70 bg-sand/40"
          : "border-border-subtle bg-white"
      )}
    >
      {resolved && (
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-turkis-fill px-2.5 py-1 text-[11.5px] font-bold text-petrol">
          <Check className="h-3 w-3" />
          {t("admin.notes.status.resolved", "Resolved")}
          {thread.resolver && ` · ${personLabel(thread.resolver)}`}
          {thread.resolved_at && (
            <span
              className="font-medium opacity-70"
              title={absoluteTime(thread.resolved_at)}
            >
              {relativeTime(thread.resolved_at)}
            </span>
          )}
        </div>
      )}

      <div className={cn(resolved && "opacity-75")}>
        <NoteEntry
          note={thread}
          scope={scope}
          canWrite={canWrite}
          canModify={canModify(thread)}
          onReply={() => openThread(true)}
          resolved={resolved}
          onToggleResolve={toggleResolved}
          resolveBusy={resolve.isPending}
        />
      </div>

      {/* Folded: the one line that says a conversation happened here. */}
      {replyCount > 0 && !open && (
        <ReplySummary
          replies={thread.replies}
          onOpen={() => openThread(false)}
        />
      )}

      {open && (
        <div className="mt-3 space-y-3 border-l-2 border-turkis-fill pl-3.5 sm:ml-4">
          {thread.replies.map((reply) => (
            <NoteEntry
              key={reply.id}
              note={reply}
              scope={scope}
              size="sm"
              canWrite={canWrite}
              canModify={canModify(reply)}
            />
          ))}

          {/* An open thread always carries its composer — the point of opening
              one is to answer it, and a second "reply" button to get there is a
              click that buys nothing. */}
          {canWrite && (
            <Composer
              scope={scope}
              parentId={thread.id}
              placeholder={t(
                "admin.notes.replyInThreadPlaceholder",
                "Reply in thread…"
              )}
              autoFocus={replying}
              compact
              onDone={() => setReplying(false)}
              // With no replies yet, the thread only exists because you pressed
              // Reply, so backing out of the composer should close it again.
              onCancel={
                replyCount === 0
                  ? () => {
                      setReplying(false);
                      setOpen(false);
                    }
                  : undefined
              }
            />
          )}

          {replyCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setReplying(false);
              }}
              className="block text-[12.5px] font-semibold text-muted-foreground hover:text-body"
            >
              {t("admin.notes.hideThread", "Hide thread")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The folded thread — Slack's "3 replies · last reply 2 hours ago", with the
 * faces of whoever took part. It carries enough to decide whether to open it.
 */
function ReplySummary({
  replies,
  onOpen,
}: {
  replies: NoteRow[];
  onOpen: () => void;
}) {
  const { t } = useLanguage();
  const participants = useMemo(() => {
    const seen = new Set<string>();
    const out: NoteRow["author"][] = [];
    for (const r of replies) {
      const key = r.created_by ?? r.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r.author);
      if (out.length === 3) break;
    }
    return out;
  }, [replies]);

  const last = replies[replies.length - 1];

  return (
    <button
      type="button"
      onClick={onOpen}
      className="mt-2 flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-turkis-fill-soft sm:ml-12 sm:w-auto"
    >
      <span className="flex -space-x-1.5">
        {participants.map((p, i) => (
          <NoteAvatar
            key={p?.id ?? i}
            person={p}
            size="sm"
            className="h-5 w-5 text-[9px] ring-2 ring-white"
          />
        ))}
      </span>
      <span className="text-[12.5px] font-bold text-turkis-strong">
        {replies.length}{" "}
        {replies.length === 1
          ? t("admin.notes.replySingular", "reply")
          : t("admin.notes.repliesPlural", "replies")}
      </span>
      {last && (
        <span
          className="text-[12px] text-muted-foreground"
          title={absoluteTime(last.created_at)}
        >
          {t("admin.notes.lastReply", "Last reply")}{" "}
          {relativeTime(last.created_at)}
        </span>
      )}
    </button>
  );
}

// ─── Feed ───────────────────────────────────────────────────────────────────

export function NotesThread({
  scope,
  /** One line naming what belongs here, shown when the feed is empty. */
  emptyHint,
  className,
}: {
  scope: NoteScope;
  emptyHint?: string;
  className?: string;
}) {
  const { user, isSuperAdmin, canManageTenant } = useAuth();
  const { t } = useLanguage();
  const { data, isLoading, isError, error, refetch } = useEntityNotes(scope);
  const [filter, setFilter] = useState<Filter>("open");

  const resolvedEmptyHint =
    emptyHint ??
    t(
      "admin.notes.defaultEmptyHint",
      "Anything the next admin should know about this record — access quirks, verbal agreements, open follow-ups."
    );

  const FILTERS: Array<{ id: Filter; label: string }> = [
    { id: "open", label: t("admin.notes.filter.open", "Open") },
    { id: "resolved", label: t("admin.notes.filter.resolved", "Resolved") },
    { id: "saved", label: t("admin.notes.filter.saved", "Saved") },
    { id: "all", label: t("admin.notes.filter.all", "All") },
  ];

  // Writing is admin / super-admin (`canManageTenant`). Reading is every staff
  // role the RLS policy admits, so tech keeps its read-only view.
  const canWrite = canManageTenant;

  const canModify = useCallback(
    (note: NoteRow) =>
      canManageTenant && (isSuperAdmin || note.created_by === user?.id),
    [canManageTenant, isSuperAdmin, user?.id]
  );

  const { open, resolved, saved } = useMemo(() => {
    const o: NoteThreadModel[] = [];
    const r: NoteThreadModel[] = [];
    // A thread counts as saved if you bookmarked the note OR any of its
    // replies — you saved the conversation, not a row id.
    const s = data.threads.filter(
      (thread) => thread.saved || thread.replies.some((x) => x.saved)
    );
    for (const thread of data.threads)
      (thread.resolved_at ? r : o).push(thread);
    return { open: o, resolved: r, saved: s };
  }, [data.threads]);

  const visible =
    filter === "open"
      ? open
      : filter === "resolved"
        ? resolved
        : filter === "saved"
          ? saved
          : data.threads;

  if (isError) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-destructive/30 bg-destructive/5 p-5",
          className
        )}
      >
        <p className="text-sm font-semibold text-destructive">
          {t("admin.notes.loadError", "Notes could not be loaded.")}
        </p>
        <p className="mt-1 text-[13px] text-body-soft">
          {errorText(error, t("admin.notes.unknownError", "Unknown error."))}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => refetch()}
        >
          {t("admin.notes.tryAgain", "Try again")}
        </Button>
      </div>
    );
  }

  // The scope's columns do not exist on this database yet. Say that plainly
  // instead of rendering an empty feed that looks like "no notes".
  if (!isLoading && !data.schemaReady && scope.level !== "tenant") {
    return (
      <div
        className={cn(
          "rounded-2xl border border-dashed border-border-subtle bg-sand/50 p-6 text-center",
          className
        )}
      >
        <StickyNote className="mx-auto h-6 w-6 text-muted-foreground" />
        <p className="mt-2 text-sm font-semibold text-heading">
          {t(
            "admin.notes.schemaNotReady.title",
            "Notes are not available on this environment yet"
          )}
        </p>
        <p className="mx-auto mt-1 max-w-md text-[13px] leading-relaxed text-body-soft">
          {t(
            "admin.notes.schemaNotReady.missingMigration",
            "This database is missing migration"
          )}{" "}
          <code className="rounded bg-white px-1 py-0.5 text-[12px]">
            {/* Naming the migration the OWNER feed needs, rather than the one
                that added the other levels, is the difference between the
                instruction working and not. */}
            {scope.level === "owner"
              ? "20260816130000_admin_notes_owner_scope"
              : "20260812100000_admin_notes_entity_scope_threads"}
          </code>
          . {t("admin.notes.schemaNotReady.run", "Run")}{" "}
          <code className="rounded bg-white px-1 py-0.5 text-[12px]">
            supabase db push
          </code>{" "}
          {t("admin.notes.schemaNotReady.againstIt", "against it.")}
        </p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {canWrite && (
        <Composer
          scope={scope}
          placeholder={t("admin.notes.addNotePlaceholder", "Add a note…")}
        />
      )}

      {data.threads.length > 0 && (
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => {
            const count =
              f.id === "open"
                ? open.length
                : f.id === "resolved"
                  ? resolved.length
                  : f.id === "saved"
                    ? saved.length
                    : data.threads.length;
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-pressed={active}
                className={cn(
                  "rounded-full px-3 py-1 text-[12.5px] font-semibold transition-colors",
                  active
                    ? "bg-petrol text-white"
                    : "text-muted-foreground hover:bg-sand hover:text-body"
                )}
              >
                {f.label}
                <span className="ml-1 tabular-nums opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="flex gap-3 rounded-2xl border border-border-subtle bg-white p-3.5"
            >
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyFeed
          filter={filter}
          hasAny={data.threads.length > 0}
          resolvedCount={resolved.length}
          canWrite={canWrite}
          emptyHint={resolvedEmptyHint}
          onShowAll={() => setFilter("all")}
        />
      ) : (
        <div className="space-y-3">
          {visible.map((thread) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              scope={scope}
              canWrite={canWrite}
              canModify={canModify}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyFeed({
  filter,
  hasAny,
  resolvedCount,
  canWrite,
  emptyHint,
  onShowAll,
}: {
  filter: Filter;
  hasAny: boolean;
  resolvedCount: number;
  canWrite: boolean;
  emptyHint: string;
  onShowAll: () => void;
}) {
  const { t } = useLanguage();

  const filterLabel =
    filter === "open"
      ? t("admin.notes.filter.open", "Open")
      : filter === "resolved"
        ? t("admin.notes.filter.resolved", "Resolved")
        : filter === "saved"
          ? t("admin.notes.filter.saved", "Saved")
          : t("admin.notes.filter.all", "All");

  if (hasAny && filter === "open") {
    return (
      <div className="rounded-2xl border border-dashed border-border-subtle bg-sand/40 px-5 py-8 text-center">
        <Check className="mx-auto h-6 w-6 text-turkis-strong" />
        <p className="mt-2 text-sm font-semibold text-heading">
          {t("admin.notes.emptyFeed.nothingOpen", "Nothing open here")}
        </p>
        <p className="mt-1 text-[13px] text-body-soft">
          {resolvedCount}{" "}
          {resolvedCount === 1
            ? t("admin.notes.emptyFeed.resolvedNoteSingular", "resolved note")
            : t(
                "admin.notes.emptyFeed.resolvedNotePlural",
                "resolved notes"
              )}{" "}
          {t("admin.notes.emptyFeed.onThisRecord", "on this record.")}
        </p>
        <button
          type="button"
          onClick={onShowAll}
          className="mt-2 text-[13px] font-semibold text-turkis-strong hover:underline"
        >
          {t("admin.notes.emptyFeed.showAll", "Show all")}
        </button>
      </div>
    );
  }

  if (hasAny) {
    return (
      <div className="rounded-2xl border border-dashed border-border-subtle bg-sand/40 px-5 py-8 text-center text-[13px] text-body-soft">
        {t(
          "admin.notes.emptyFeed.noFilteredNotes",
          "No {filter} notes."
        ).replace("{filter}", filterLabel.toLowerCase())}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-dashed border-border-subtle bg-sand/40 px-5 py-10 text-center">
      <MessageSquarePlus className="mx-auto h-6 w-6 text-muted-foreground" />
      <p className="mt-2 text-sm font-semibold text-heading">
        {t("admin.notes.emptyFeed.noNotesYet", "No notes yet")}
      </p>
      <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-body-soft">
        {canWrite
          ? emptyHint
          : t(
              "admin.notes.emptyFeed.readOnlyHint",
              "Notes added by the admin team will appear here."
            )}
      </p>
    </div>
  );
}
